import * as React from "react";
import { RouterProvider } from "react-router";
import {
  QueryClient,
  QueryClientProvider,
  focusManager,
} from "@tanstack/react-query";
import { Toaster } from "sonner";
import { router } from "@/router";
import { mobileRouter } from "@/router.mobile";
import { useThemeSync } from "@/lib/theme";
import { useAuthStore } from "@/store/authStore";
import { BrandingProvider } from "@/components/layout/BrandingProvider";
import { isMobileBuild } from "@/platform";
import { PlatformProvider, usePlatform } from "@/platform/usePlatform";
import { OfflineBanner } from "@/components/layout/OfflineBanner";
import { initNetworkListener } from "@/native/network";
import { usePushRegistration } from "@/native/usePushRegistration";
import { useCapgoUpdate } from "@/native/useCapgoUpdate";
import { useTimezoneCapture } from "@/lib/useTimezoneCapture";
import { useViewport } from "@/lib/useViewport";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30 * 1000,
    },
  },
});

/** MV2-368: side-effect-only component mounted inside QueryClientProvider
 *  so it can use React Query hooks. Lives here (not next to usePushRegistration
 *  in AppInner) because the latter doesn't depend on /me data. */
function QuerySideEffects(): null {
  useTimezoneCapture();
  return null;
}

function AppInner(): React.ReactElement {
  useThemeSync();
  const initialize = useAuthStore((s) => s.initialize);
  const { setIsOnline, isNative } = usePlatform();
  const { isMobile } = useViewport();

  React.useEffect(() => {
    void initialize();
  }, [initialize]);

  // Start network listener once, after platform context is available
  React.useEffect(() => initNetworkListener(setIsOnline), [setIsOnline]);

  // Keep TanStack Query's focus tracking in sync with Capacitor's real
  // foreground state. WKWebView doesn't reliably fire `visibilitychange`
  // back to "visible" on resume, so without this, `refetchOnWindowFocus`
  // and `refetchInterval`'s background-pause (both keyed off focusManager)
  // can get stuck thinking the app is still unfocused after a background
  // trip — silently freezing the 30s REST poll fallback the live pages
  // rely on (see IoTClient's own appStateChange wiring for the WSS half).
  React.useEffect(() => {
    if (!isNative) return;
    let cancelled = false;
    let handle: { remove: () => void } | undefined;
    void import("@capacitor/app").then(({ App: CapApp }) => {
      if (cancelled) return;
      void CapApp.addListener("appStateChange", ({ isActive }) => {
        focusManager.setFocused(isActive);
      }).then((h) => {
        if (cancelled) h.remove();
        else handle = h;
      });
    });
    return () => {
      cancelled = true;
      handle?.remove();
    };
  }, [isNative]);

  // Register for push only after Amplify session is confirmed — ensures the
  // Cognito token is valid when POST /me/devices is called.
  usePushRegistration();

  // Listen for Capgo bundle download completion and surface a "Restart now"
  // toast so users can apply the update at a convenient moment.
  useCapgoUpdate();

  return (
    <QueryClientProvider client={queryClient}>
      <QuerySideEffects />
      <BrandingProvider>
        <OfflineBanner />
        {/* MV2-XXXX — mobile builds route through their own isolated tree
            (src/router.mobile.tsx) so a web page/component edit can never
            reach a mobile screen. __MOBILE_BUILD__ is a Vite compile-time
            define (see vite.config.ts), so each build only ever mounts one
            of these two routers. */}
        <RouterProvider router={isMobileBuild ? mobileRouter : router} />

        {isMobile ? (
          /**
           * Mobile toast — native-feeling pill centred above the bottom nav.
           *
           * Design decisions:
           *   • top-center, below the status bar (ds/Dialogs "Toast stack",
           *     MV2-840): the bottom nav owns the bottom edge and a toast there
           *     covered it. Three visible, the rest queue.
           *   • No close button — native iOS/Android toasts auto-dismiss.
           *   • Wide (calc 100 vw − 32 px) so text has room without wrapping.
           *   • border-radius 20 px reads as a pill / "notification chip".
           *   • font 15 px / 500 weight matches iOS system notification text.
           *   • expand=true shows all queued toasts instead of stacking into a pile —
           *     helps the operator see every alarm even when several fire at once.
           *   • gap=8 gives breathing room between stacked toasts.
           */
          <Toaster
            position="top-center"
            richColors
            closeButton={false}
            expand
            gap={8}
            visibleToasts={3}
            mobileOffset={{ top: "calc(env(safe-area-inset-top) + 12px)" }}
            toastOptions={{
              style: {
                borderRadius: "20px",
                padding: "14px 18px",
                fontSize: "15px",
                fontWeight: "500",
                width: "calc(100vw - 32px)",
                maxWidth: "420px",
                boxShadow:
                  "0 8px 24px rgba(0,0,0,0.14), 0 2px 6px rgba(0,0,0,0.08)",
              },
              classNames: {
                toast: "tabular",
              },
            }}
          />
        ) : (
          /* Desktop toast — the board kit `.toast` (ds/Dialogs and toasts · ops/AlarmsToast):
             success is the dark bar (gray-900 on gray-0, 10 px radius, 11×13 padding, 13 px,
             deep shadow) with a bold action-colour text action; neutral/info is the surface
             card with a gray-200 border; error is the surface card with a danger border and
             a mono description line. No richColors (Kranthi 9 Sep: one toast vocabulary). */
          <Toaster
            position="top-right"
            closeButton
            visibleToasts={3}
            gap={9}
            toastOptions={{
              classNames: {
                toast:
                  "tabular !rounded-[10px] !border-0 !bg-gray-900 !px-[13px] !py-[11px] !text-[13px] !text-gray-0 !shadow-[0_10px_30px_rgba(2,6,23,.24)] !gap-2.5 !items-center",
                title: "!font-normal !text-[13px]",
                description: "!mt-0.5 !font-mono !text-[10.5px] !text-gray-500",
                default: "!border !border-gray-200 !bg-surface !text-gray-900",
                info: "!border !border-gray-200 !bg-surface !text-gray-900",
                warning: "!border !border-[color:var(--warning)] !bg-surface !text-gray-900",
                error: "!border !border-[color:var(--danger)] !bg-surface !text-gray-900 [&_[data-icon]]:!text-[color:var(--danger)]",
                success: "[&_[data-icon]]:!text-[color:var(--success)]",
                icon: "!m-0",
                actionButton:
                  "!h-auto !bg-transparent !p-0 !text-[13px] !font-semibold !text-[color:var(--action)] hover:!underline",
                cancelButton: "!h-auto !bg-transparent !p-0 !text-[13px] !text-gray-500 hover:!underline",
                closeButton: "!border-gray-200 !bg-surface !text-gray-600",
              },
            }}
          />
        )}
      </BrandingProvider>
    </QueryClientProvider>
  );
}

export function App(): React.ReactElement {
  return (
    <PlatformProvider>
      <AppInner />
    </PlatformProvider>
  );
}
